import { type WorldObject, specOf } from '@tama/game-core';
import type { Graphics } from 'pixi.js';
import { TILE } from './layout';
import { C } from './palette';

/**
 * The furniture catalogue, drawn top-down.
 *
 * Each function is handed the pixel rectangle its object's footprint occupies
 * and draws inside it, so a piece moved in dev mode needs no per-object
 * bookkeeping here: change the tile, the rectangle follows.
 *
 * Sizes come from `OBJECT_SPECS`, which is also what decides collision — a
 * table that looks two tiles wide is two tiles wide to walk around.
 */
export function drawObject(g: Graphics, object: WorldObject): void {
  const spec = specOf(object);
  const x = object.x * TILE;
  const y = object.y * TILE;
  const w = spec.width * TILE;
  const h = spec.height * TILE;

  switch (object.kind) {
    case 'bed':
      return drawBed(g, x, y, w, h);
    case 'table':
      return drawTable(g, x, y, w, h);
    case 'chair':
      return drawChair(g, x, y, w, h);
    case 'lamp':
      return drawLamp(g, x, y, w, h);
    case 'rug':
      return drawRug(g, x, y, w, h);
    case 'chest':
      return drawChest(g, x, y, w, h);
    case 'plant':
      return drawPlant(g, x, y, w, h);
  }
}

/**
 * A soft contact shadow, so furniture sits on the floor instead of floating.
 *
 * An ellipse tucked under the piece rather than a bar across its footprint: a
 * bar reads as part of the object — a step, or a shelf — at these sizes.
 */
function contactShadow(g: Graphics, x: number, y: number, w: number, h: number): void {
  g.ellipse(x + w / 2, y + h - 4, w * 0.42, 4.5).fill({ color: C.wallShadow, alpha: 0.14 });
}

/** Seen from above: headboard, pillow, then the duvet turned down over the sheet. */
function drawBed(g: Graphics, x: number, y: number, w: number, h: number): void {
  contactShadow(g, x, y, w, h);

  g.roundRect(x + 2, y + 2, w - 4, h - 4, 7).fill(C.bedFrame);
  // A darker band across the top gives the bed a head, which is the only thing
  // that says which way round it is.
  g.roundRect(x + 2, y + 2, w - 4, 10, 6).fill(C.bedFrameDark);

  g.roundRect(x + 5, y + 12, w - 10, h - 17, 5).fill(C.bedSheet);

  const pillowH = Math.max(8, h * 0.15);
  g.roundRect(x + 8, y + 15, w - 16, pillowH, 4).fill(C.bedPillow);
  g.roundRect(x + 8, y + 15, w - 16, pillowH, 4).stroke({ color: C.bedPillowShade, width: 1.5 });

  // Duvet over the lower half, with the sheet turned down over its top edge.
  const duvetTop = y + h * 0.44;
  g.roundRect(x + 5, duvetTop, w - 10, y + h - 7 - duvetTop, 5).fill(C.bedBlanket);
  g.roundRect(x + 5, duvetTop, w - 10, 7, 4).fill(C.bedBlanketFold);
}

function drawTable(g: Graphics, x: number, y: number, w: number, h: number): void {
  contactShadow(g, x, y, w, h);

  g.roundRect(x + 2, y + 3, w - 4, h - 6, 6).fill(C.woodDark);
  g.roundRect(x + 4, y + 2, w - 8, h - 8, 5).fill(C.wood);
  // Two grain lines: enough to say "wood" at 64 px across.
  for (const t of [0.36, 0.64]) {
    g.rect(x + 8, y + 2 + (h - 8) * t, w - 16, 1.5).fill({ color: C.woodDark, alpha: 0.4 });
  }
  g.roundRect(x + 8, y + 6, w - 16, 4, 2).fill({ color: C.woodLight, alpha: 0.7 });
}

function drawChair(g: Graphics, x: number, y: number, w: number, h: number): void {
  contactShadow(g, x, y, w, h);

  // Backrest first, as a band behind the seat: from above that overlap is the
  // only thing separating a chair from a stool.
  g.roundRect(x + 4, y + 3, w - 8, 8, 4).fill(C.woodDark);
  g.roundRect(x + 6, y + 8, w - 12, h - 16, 4).fill(C.wood);
  g.roundRect(x + 9, y + 11, w - 18, 3, 1.5).fill({ color: C.woodLight, alpha: 0.75 });
}

function drawLamp(g: Graphics, x: number, y: number, w: number, h: number): void {
  const cx = x + w / 2;
  const cy = y + h / 2;

  // The pool of light first, so everything else sits inside it.
  g.circle(cx, cy, TILE * 0.62).fill({ color: C.lampGlow, alpha: 0.18 });
  g.circle(cx, cy, TILE * 0.42).fill({ color: C.lampGlow, alpha: 0.22 });

  g.ellipse(cx, cy + 7, w * 0.28, 5).fill(C.lampBase);
  g.circle(cx, cy, TILE * 0.3).fill(C.lampShade);
  g.circle(cx, cy - 2, TILE * 0.18).fill(C.lampGlow);
}

/** Walkable, so it is drawn flat and never casts a shadow. */
function drawRug(g: Graphics, x: number, y: number, w: number, h: number): void {
  const cx = x + w / 2;
  const cy = y + h / 2;

  g.ellipse(cx, cy, w / 2 - 3, h / 2 - 3).fill(C.rug);
  g.ellipse(cx, cy, w / 2 - 9, h / 2 - 9).fill(C.rugInner);
  for (const [dx, dy] of [
    [-0.26, 0],
    [0.26, 0],
    [0, -0.26],
    [0, 0.26],
  ]) {
    g.circle(cx + dx! * w, cy + dy! * h, 3).fill({ color: C.rugDot, alpha: 0.85 });
  }
}

function drawChest(g: Graphics, x: number, y: number, w: number, h: number): void {
  contactShadow(g, x, y, w, h);

  g.roundRect(x + 4, y + 5, w - 8, h - 12, 4).fill(C.chest);
  g.roundRect(x + 4, y + 5, w - 8, (h - 12) * 0.45, 4).fill(C.chestLid);
  g.rect(x + w / 2 - 2, y + 5, 4, h - 12).fill(C.chestIron);
  g.roundRect(x + w / 2 - 4, y + h * 0.42, 8, 5, 2).fill(C.chestIron);
}

function drawPlant(g: Graphics, x: number, y: number, w: number, h: number): void {
  const cx = x + w / 2;
  const cy = y + h / 2;

  contactShadow(g, x, y, w, h);
  g.roundRect(cx - 7, cy + 1, 14, 11, 3).fill(C.pot);
  g.ellipse(cx, cy - 4, 6, 9).fill(C.plant);
  g.ellipse(cx - 7, cy - 1, 5, 7).fill(C.plantDark);
  g.ellipse(cx + 7, cy - 1, 5, 7).fill(C.plantDark);
}
